import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import Dropdown from '@/components/common/Dropdown.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import useIcons from '@/composables/useIcons'
  import useEditor from '@/composables/useEditor'
  import UsersApi from '@/services/api/UsersApi'
  import { UserModule } from '@/store/modules/user'
  import useI18n from '@/composables/useI18n'
  import eventBus from '@/main'
  import type { GamificationActivity, UserGamificationActivity, UserLevel } from '@/services/interfaces/Auth0'
  import { useRoute } from 'vue-router/composables'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'EditGamification',
  setup(__props) {

  const route = useRoute()
  const { checkmark, trashIcon } = useIcons()
  const { hasLocalChanges } = useEditor(route)
  const { translateString } = useI18n()

  const fetchingData = ref(false)
  const gamificationLevels = ref([] as UserLevel[])
  const previousGamificationLevels = ref([] as UserLevel[])
  const gamificationActivities = ref([] as GamificationActivity[])
  const previousGamificationActivities = ref(
    [] as { id: string; name: string; points: number; subtract: boolean; repeatable: boolean }[],
  )
  const allGamificationActivities = ref([] as GamificationActivity[])
  const gainOrRemoveItems = ref([
    { type: false, title: translateString('editor.editGamification.gain'), selectable: true },
    { type: true, title: translateString('editor.editGamification.remove'), selectable: true },
  ])
  const applyingLevelChanges = ref(false)
  const applyingActivityChanges = ref(false)

  const gamificationActivitiesDropdownItems = computed(() => {
    return (activityId: string) => {
      if (!activityId)
        return allGamificationActivities.value.map((a) => ({
          type: a.gamificationActivityId,
          title: a.name,
          selectable: !activityTrackedBefore.value(a.gamificationActivityId),
        }))
      const filteredActivities = gamificationActivities.value.filter((a) => a.name.length)
      return filteredActivities.map((a) => ({
        type: a.gamificationActivityId,
        title: a.name,
        selectable: a.gamificationActivityId === activityId,
      }))
    }
  })

  const activityTrackedBefore = computed(() => {
    return (activityId: string) => {
      return !!gamificationActivities.value.find((a) => a.gamificationActivityId === activityId && a._destroy !== true)
    }
  })

  const hasLevelChanges = computed(() => {
    return hasLocalChanges.value(previousGamificationLevels.value, gamificationLevels.value)
  })

  const hasActivityChanges = computed(() => {
    return hasLocalChanges.value(previousGamificationActivities.value, gamificationActivities.value)
  })

  const companyId = computed(() => UserModule.currentCompany!.id)

  const filteredGamificationLevels = computed(() => gamificationLevels.value.filter((l) => l._destroy !== true))

  const filteredGamificationActivities = computed(() => gamificationActivities.value.filter((a) => a._destroy !== true))

  const gamificationActivityName = computed(() => {
    return (activityName: UserGamificationActivity, locale: string) => {
      return translateString(`editor.editGamification.gamificationActivities.${activityName}`, {
        locale,
      })
    }
  })

  const localizedGamificationActivities = computed(() => {
    return (activities: GamificationActivity[], locale: string) => {
      return activities.map((a) => ({
        id: a.id,
        gamificationActivityId: a.gamificationActivityId,
        name: gamificationActivityName.value(a.name as UserGamificationActivity, locale),
        key: a.name as UserGamificationActivity,
        points: a.points,
        subtract: a.subtract,
        repeatable: a.repeatable,
      }))
    }
  })

  const addNewLevel = () => {
    gamificationLevels.value.push({
      id: '',
      name: '',
      maximumPoints: 0,
    })
  }

  const addGamificationAction = () => {
    gamificationActivities.value.push({
      id: '',
      gamificationActivityId: '',
      name: '',
      points: 0,
      subtract: false,
      repeatable: false,
    })
  }

  const deleteLevel = (index: number, levelId: string) => {
    if (levelId) {
      const level = gamificationLevels.value[index]
      eventBus.$set(level, '_destroy', true)
    } else {
      gamificationLevels.value.splice(index, 1)
    }
  }

  const deleteGamificationActivity = (index: number, activityId: string) => {
    if (activityId) {
      const activity = filteredGamificationActivities.value[index]
      eventBus.$set(activity, '_destroy', true)
    } else {
      filteredGamificationActivities.value.splice(index, 1)
    }
  }

  const setGamificationActivity = (activityId: string) => {
    const newActivity = allGamificationActivities.value.find((a) => a.gamificationActivityId === activityId)
    if (newActivity) {
      newActivity.id = ''
      gamificationActivities.value.splice(gamificationActivities.value.length - 1, 1, newActivity)
    }
  }

  const setGainOrRemove = (activityId: string, subtract: boolean) => {
    const activity = gamificationActivities.value.find((a) => a.gamificationActivityId === activityId)
    if (activity) activity.subtract = subtract
  }

  const applyLevelChanges = () => {
    if (hasLevelChanges.value && !applyingLevelChanges.value) {
      applyingLevelChanges.value = true
      UsersApi.updateGamificationLevelsOfACompany(companyId.value, gamificationLevels.value)
        .then(async () => {
          const currentUserLevel = gamificationLevels.value.find(
            (l) => l.id === UserModule.userGamificationStats?.level.id,
          )
          if (hasLocalChanges.value(currentUserLevel, UserModule.userGamificationStats?.level)) {
            await UserModule.getUserGamificationStats()
            UserModule.setUserGamificationLevel(structuredClone(currentUserLevel!))
          }
          eventBus.$toasted.success(translateString('editor.editGamification.levelsUpdated'))
        })
        .finally(() => {
          applyingLevelChanges.value = false
          previousGamificationLevels.value = structuredClone(gamificationLevels.value)
        })
    }
  }

  const applyActivityChanges = () => {
    // remove unfilled gamification activities
    gamificationActivities.value.forEach((a, index) => {
      if (!a.gamificationActivityId) {
        gamificationActivities.value.splice(index, 1)
      }
    })
    if (hasActivityChanges.value && !applyingActivityChanges.value) {
      applyingActivityChanges.value = true
      // we have to use the name as the key which is in en-US version
      // before we send them to the BE because that's how they're saved on the DB
      const enLocalizedActivities = gamificationActivities.value.map((a) => ({
        id: a.id,
        gamificationActivityId: a.gamificationActivityId,
        name: a.name,
        points: a.points,
        subtract: a.subtract,
        _destroy: a._destroy,
        repeatable: a.repeatable,
      }))
      const activities = structuredClone(enLocalizedActivities)
      UsersApi.updateGamificationActivitiesOfACompany(companyId.value, activities)
        .then(() => {
          getAllGamificationActivities()
          eventBus.$toasted.success(translateString('editor.editGamification.activitiesUpdated'))
        })
        .finally(() => {
          applyingActivityChanges.value = false
          previousGamificationActivities.value = structuredClone(
            localizedGamificationActivities.value(activities, eventBus.$i18n.locale),
          )
        })
    }
  }

  const getAllGamificationActivities = async () => {
    if (!(UserModule.companyGamificationActivities.length > 0)) await UserModule.getCompanyGamificationActivities()
    return (allGamificationActivities.value = localizedGamificationActivities.value(
      structuredClone(UserModule.companyGamificationActivities),
      eventBus.$i18n.locale,
    ))
  }

  const getGamificationSettingsData = () => {
    fetchingData.value = true
    const getLevels = UsersApi.getGamificationLevelsOfACompany(companyId.value).then((levels) => {
      gamificationLevels.value = levels
      previousGamificationLevels.value = structuredClone(levels)
    })
    const getActivities = UsersApi.getGamificationActivitiesOfACompany(companyId.value).then((activities) => {
      const localizedActivities = localizedGamificationActivities.value(activities, eventBus.$i18n.locale)
      gamificationActivities.value = localizedActivities
      previousGamificationActivities.value = structuredClone(localizedActivities)
    })
    const allTrackedGamificationActivities = getAllGamificationActivities()
    Promise.all([getLevels, getActivities, allTrackedGamificationActivities]).finally(
      () => (fetchingData.value = false),
    )
  }

  getGamificationSettingsData()

return { __sfc: true,route, checkmark, trashIcon, hasLocalChanges, translateString, fetchingData, gamificationLevels, previousGamificationLevels, gamificationActivities, previousGamificationActivities, allGamificationActivities, gainOrRemoveItems, applyingLevelChanges, applyingActivityChanges, gamificationActivitiesDropdownItems, activityTrackedBefore, hasLevelChanges, hasActivityChanges, companyId, filteredGamificationLevels, filteredGamificationActivities, gamificationActivityName, localizedGamificationActivities, addNewLevel, addGamificationAction, deleteLevel, deleteGamificationActivity, setGamificationActivity, setGainOrRemove, applyLevelChanges, applyActivityChanges, getAllGamificationActivities, getGamificationSettingsData, computed, ref, KetchUpButton, SVGRenderer, Dropdown, LoadingSpinner, useIcons, useEditor, UsersApi, UserModule, useI18n, eventBus, useRoute }
}

})